import { Box, ListView } from '@razorpay/blade/components';
import React, { ReactElement, useState } from 'react';

import api from 'api';
import { FormProvider } from 'forms';
import { useFilters } from 'hooks/useFilters';
import { usePagination } from 'hooks/usePagination';
import { useQuery } from 'react-query';
import SalaryStructureListView from './SalaryStructureListView';
import Filters from './components/Filters';
import { salaryStructureQueries } from './constants';
import {
  SalaryStructureCreationFlow,
  SalaryStructureFormData,
  StructureCreateType,
} from './creation';
import { filterSalaryStructures } from './utils';

const SalaryStructureView = (): ReactElement => {
  const [creationFlow, setCreationFlow] = useState<{
    isOpen: boolean;
    creationType: StructureCreateType | null;
  }>({
    isOpen: false,
    creationType: null,
  });
  // Pagination state
  const { setCurrentPage } = usePagination({
    page: 0,
    limit: 10,
  });

  // Filters state
  const { currentFilters } = useFilters({
    filtersMap: {
      status: 'status',
      search: 'search',
    },
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  // Data fetching with react-query
  const { data, isFetching, refetch, error } = useQuery({
    queryKey: [salaryStructureQueries.getSalaryStructuresList],
    queryFn: async () => {
      const result = await api.salaryStructures.queries.listSalaryStructures({
        input: {
          status: 'SALARY_STRUCTURE_STATUS_NONE',
        },
      });
      // Type assertion to handle unknown type from API
      return result.payrollListSalaryStructures.data.salaryStructures;
    },
  });

  const structures = data ?? [];

  const handleCreateClick = (creationType: StructureCreateType) => {
    // Open creation flow
    setCreationFlow({
      isOpen: true,
      creationType,
    });
  };

  return (
    <Box marginTop="spacing.5">
      <ListView>
        <Filters structures={structures} />

        <Box marginTop="spacing.7">
          <SalaryStructureListView
            structures={filterSalaryStructures(structures, currentFilters)}
            isFetching={isFetching}
            onRetry={refetch}
            error={error}
            onCreateStructure={handleCreateClick}
          />
        </Box>
      </ListView>
      {creationFlow.creationType ? (
        <FormProvider
          initialState={{ creationType: creationFlow.creationType } as SalaryStructureFormData}>
          <SalaryStructureCreationFlow
            isOpen={creationFlow.isOpen}
            onClose={() => setCreationFlow({ isOpen: false, creationType: null })}
          />
        </FormProvider>
      ) : null}
    </Box>
  );
};

export default SalaryStructureView;
